import { cloneElement, type FunctionComponent, type PropsWithChildren, useState } from 'react';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { ClickableElement } from '../../buttons';
import ArrowDropDownIcon from '../../svgs/arrow-drop-down.svg';
import CheckCircleIcon from '../../svgs/untitled-ui/check-circle.svg';
import CheckIcon from '../../svgs/untitled-ui/check.svg';
import ChevronDownIcon from '../../svgs/untitled-ui/chevron-down.svg';
import { MaybeLabel } from '../label/label.component';
import { Overlay } from '../overlay/overlay.component';
import { dropdownInsetLabel, dropdownOptions, dropdownOptionsWidth } from './dropdown-menu.css';

const MENU_CLASSES =
	'theme-luxury:rounded-2xl! theme-luxury:overflow-hidden! theme-luxury:py-2! theme-luxury:px-1.5! theme-luxury:border! theme-luxury:border-secondary! theme-luxury:shadow-lg! theme-luxury:mt-2! theme-luxury:left-0! theme-luxury:right-0!';

const ITEM_CLASSES =
	'theme-luxury:text-primary! theme-luxury:text-base! theme-luxury:font-medium! theme-luxury:p-2! theme-luxury:radius-sm theme-luxury:border-none! theme-luxury:justify-between! theme-luxury:hover:bg-primary-hover!';

const SIZE_MAP = {
	DEFAULT: `input`,
	SMALL: `input-small`,
	LARGE: `input-large`
};

const FONT_SIZE_MAP = {
	DEFAULT: `f5`,
	SMALL: `f6`,
	LARGE: `f5`
};

const TRIGGER_SIZE = {
	DEFAULT: `theme-luxury:px-4! theme-luxury:text-base! theme-luxury:py-[7px]!`,
	LARGE: `theme-luxury:px-6! theme-luxury:py-[17px]!`
};

const THEME_MAP = {
	DEFAULT: {
		labelColor: null,
		dropdownBorderColor: 'b--theme-grey-light',
		containerColors: 'b--theme-grey-light theme-grey-darker bg-theme-white',
		selectedColors: null,
		itemColors: 'b--theme-grey-light hover-bg-theme-grey-lighter',
		selectedIconColor: null
	},
	DEFAULT_NO_BACKGROUND: {
		labelColor: null,
		dropdownBorderColor: 'b--theme-grey-light',
		containerColors: 'b--theme-grey-light theme-grey-darker',
		selectedColors: null,
		itemColors: 'b--theme-grey-light hover-bg-theme-grey-lighter',
		selectedIconColor: null
	},
	PRIMARY: {
		labelColor: null,
		dropdownBorderColor: 'b--theme-grey',
		containerColors: 'b--theme-grey theme-grey-darker bg-theme-white',
		selectedColors: 'bg-theme-primary-lighter theme-primary-darker',
		itemColors: 'hover-bg-theme-grey-lighter hover-theme-primary',
		selectedIconColor: 'theme-primary'
	},
	INTERNAL: {
		labelColor: 'theme-internal',
		dropdownBorderColor: 'b--theme-grey',
		containerColors: 'b--theme-internal theme-internal bg-theme-white',
		selectedColors: 'bg-theme-internal-lighter theme-internal-darker',
		itemColors: 'hover-bg-theme-grey-lighter hover-theme-primary',
		selectedIconColor: 'theme-internal'
	}
};
export const DROPDOWN_CLASSES = 'ba br2 theme-luxury:border-none! pointer relative';

export type DropdownMenuSize = keyof typeof SIZE_MAP | null;

export type DropdownMenuSelectProps = {
	toggle: () => void;
	expanded: boolean;
	size?: NonNullable<DropdownMenuSize>;
	className?: string;
	triggerSizeClassName?: string;
};

export const DropdownMenuSelect: FunctionComponent<PropsWithChildren<DropdownMenuSelectProps>> = ({
	expanded,
	toggle,
	size = 'DEFAULT',
	children,
	className,
	triggerSizeClassName = TRIGGER_SIZE[size]
}) => {
	const customClassName = className ? className : `${SIZE_MAP[size]} ph3 flex justify-between items-center`;
	return (
		<div
			className={`${customClassName} theme-luxury:border! theme-luxury:border-primary! theme-luxury:rounded-3xl! theme-luxury:focus-visible:focus-shadow-inset! theme-luxury:focus-visible:outline-none! ${triggerSizeClassName}`}
			onClick={toggle}
			onKeyPress={handleKeys(['Enter', ' '], toggle)}
			role="button"
			data-testid="dropdown-button"
			aria-expanded={expanded}
			aria-haspopup="listbox"
			tabIndex={0}>
			{children}
		</div>
	);
};

export type DropdownMenuProps = {
	title: string | JSX.Element;
	shouldCloseOnChoice?: boolean;
	size?: NonNullable<DropdownMenuSize>;
	children: React.ReactNode[];
	ariaLabel?: string;
	automationHook?: string;
	contained?: boolean;
	containerClassName?: string;
	itemClassName?: string;
	menuSelectClassName?: string;
	triggerSizeClassName?: string;
	menuClassName?: string;
	dropdownClassName?: string;
	wrapperClassName?: string;
	titlePrefix?: string;
	label?: string;
	labelHelpTextTooltip?: React.ReactElement | string;
	insetLabel?: boolean;
	initialSelectedIndex?: number;
	selectedIndex?: number | null;
	onChooseItem?: (selectedIndex: number) => void;
	theme?: keyof typeof THEME_MAP;
	borderlessItems?: boolean;
	showChevron?: boolean;
	customOptionsWidth?: 'w1' | 'w2' | 'w3' | 'w4' | 'w5';
	disableCheckMark?: boolean;
	onToggle?: () => void;
	required?: boolean;
};

const getDropdownItemClassName = ({
	index,
	childrenLength,
	selectedIndex,
	borderlessItems,
	theme,
	itemClassName
}: {
	index: number;
	childrenLength: number;
	selectedIndex: number | null | undefined;
	borderlessItems: boolean;
	theme: keyof typeof THEME_MAP;
	itemClassName: string;
}): string => {
	const classes = ['flex', 'items-center', itemClassName];

	if (!borderlessItems && index > 0) {
		classes.push('bt');
	}
	if (theme === 'DEFAULT' && index === 0) {
		classes.push('rounded-t');
	}
	if (theme === 'DEFAULT' && index === childrenLength - 1) {
		classes.push('rounded-b');
	}

	classes.push(ITEM_CLASSES);
	if (index === selectedIndex) {
		const selectedColors = THEME_MAP[theme].selectedColors;
		if (selectedColors) {
			classes.push(selectedColors);
		}
		classes.push('theme-luxury:bg-primary-hover!');
	} else {
		classes.push(THEME_MAP[theme].itemColors);
	}

	return classes.join(' ');
};

export const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({
	title,
	size = 'DEFAULT',
	shouldCloseOnChoice = false,
	ariaLabel,
	contained,
	children,
	automationHook,
	containerClassName = DROPDOWN_CLASSES,
	itemClassName = 'w-100',
	menuSelectClassName,
	triggerSizeClassName = TRIGGER_SIZE[size],
	menuClassName = MENU_CLASSES,
	dropdownClassName = '',
	wrapperClassName = '',
	titlePrefix,
	label,
	labelHelpTextTooltip,
	insetLabel = false,
	initialSelectedIndex,
	selectedIndex: controlledSelectedIndex,
	onChooseItem,
	theme = 'DEFAULT',
	borderlessItems = false,
	showChevron = true,
	customOptionsWidth,
	disableCheckMark = false,
	required,
	onToggle
}) => {
	const [expanded, setExpanded] = useState<boolean>(false);
	const [uncontrolledSelectedIndex, setUncontrolledSelectedIndex] = useState<number | undefined>(initialSelectedIndex);
	const selectedIndex = controlledSelectedIndex === undefined ? uncontrolledSelectedIndex : controlledSelectedIndex;

	const toggleMenu = () => {
		setExpanded(!expanded);
		onToggle?.();
	};

	const chooseItem = (index: number) => {
		if (controlledSelectedIndex === undefined) {
			setUncontrolledSelectedIndex(index);
		}
		if (shouldCloseOnChoice) {
			setExpanded(false);
		}
		if (onChooseItem) {
			onChooseItem(index);
		}
	};

	const expand = expanded ? '' : 'dn';
	const position = contained ? 'w-100' : `absolute ${dropdownOptions}`;
	const optionsWidth = customOptionsWidth ?? dropdownOptionsWidth;
	const widthClass = customOptionsWidth ? '' : 'theme-luxury:w-full!';
	const height = contained ? `${children?.length > 6 ? 'h5 theme-luxury:h-auto!' : ''} overflow-auto` : '';
	const chevronTransitionClass = `transition-transform duration-150 ease-linear ${expanded ? 'rotate-180' : ''}`;

	return (
		<div className={insetLabel ? 'relative mt1 theme-luxury:mt-0! theme-luxury:w-full!' : `w-100 ${wrapperClassName}`}>
			<MaybeLabel
				label={label}
				helpTextTooltip={labelHelpTextTooltip}
				required={required}
				className={`${THEME_MAP[theme].labelColor} ${
					insetLabel
						? `absolute bg-theme-white w-auto ph1 z-1 ${dropdownInsetLabel} theme-luxury:static! theme-luxury:p-0! theme-luxury:z-0!`
						: ''
				}`}
			/>
			<div
				className={`${FONT_SIZE_MAP[size]} ${THEME_MAP[theme].containerColors} ${containerClassName} theme-luxury:bg-transparent! theme-luxury:border-none!`}
				data-automation={generateDataSelector('select', automationHook)}>
				<DropdownMenuSelect
					expanded={expanded}
					toggle={toggleMenu}
					size={size}
					className={menuSelectClassName}
					triggerSizeClassName={triggerSizeClassName}>
					<span>
						{titlePrefix}
						{title}
					</span>
					{showChevron &&
						(theme === 'DEFAULT' ? (
							<ChevronDownIcon
								className={`flex-end md:ml-2 text-2xl -my-1.5 theme-luxury:fg-quaternary! theme-luxury:size-5! ${chevronTransitionClass}`}
								data-testid="ChevronDownIcon"
							/>
						) : (
							<ArrowDropDownIcon
								className={`flex-end ml2 theme-luxury:mr-2! theme-luxury:fg-quaternary! theme-luxury:size-5! ${chevronTransitionClass}`}
							/>
						))}
				</DropdownMenuSelect>
				{expanded && !contained && <Overlay style="neutral" onClick={toggleMenu} zIndex="1" />}
				<div
					className={`${position} ${optionsWidth} ${height} ${expand} z-2 ba br2 mt1 theme-grey-darker bg-theme-white ${THEME_MAP[theme].dropdownBorderColor} ${dropdownClassName} ${menuClassName} ${widthClass}`}
					data-testid="menu-list">
					<ul className="list ma0 pa0 theme-luxury:max-h-75! theme-luxury:overflow-auto! theme-luxury:p-1!">
						{children &&
							children.map((option, index) => (
								<li key={index}>
									<ClickableElement
										ariaLabel={`${ariaLabel} option ${index}`}
										aria-selected={index === selectedIndex}
										onClick={() => chooseItem(index)}
										onKeyDown={handleKeys(['Enter', ' '], () => chooseItem(index))}
										className={getDropdownItemClassName({
											index,
											childrenLength: children.length,
											selectedIndex,
											borderlessItems,
											theme,
											itemClassName
										})}>
										<>
											{option}
											{index === selectedIndex && !disableCheckMark && (
												<>
													<CheckCircleIcon
														className={`mr2 theme-luxury:hidden! ${THEME_MAP[theme].selectedIconColor}`}
													/>
													<CheckIcon className="hidden ml2 size-4 shrink-0 theme-luxury:block!" />
												</>
											)}
										</>
									</ClickableElement>
								</li>
							))}
					</ul>
				</div>
			</div>
		</div>
	);
};

export type DropdownItemProps = {
	title?: string;
	children: JSX.Element;
	tabIndex?: number;
	withoutPadding?: boolean;
};

export const DropdownMenuItem: FunctionComponent<DropdownItemProps> = ({ title, children, tabIndex = -1, withoutPadding = false }) => {
	if (title) {
		const triggerElement = (
			<div className="pa2 theme-luxury:p-0!" data-automation={generateDataSelector('button', title)}>
				{title}
			</div>
		);
		return cloneElement(children, { triggerElement, ...children.props });
	}

	return (
		<div className={`${withoutPadding ? 'pa0' : 'pv2 ph4 theme-luxury:p-0!'} w-100`} tabIndex={tabIndex}>
			{children}
		</div>
	);
};
